import React from 'react'
import { Card, Col, DatePicker, Form, Input, Row, Select, theme, Typography, Space } from 'antd'
import EntitySelect from './EntitySelect'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faUser,
  faUserMd,
  faBuilding,
  faCalendarAlt,
  faFileInvoiceDollar,
  faKey
} from '@fortawesome/free-solid-svg-icons'

/**
 * Component for handling invoice patient and practitioner details form
 */
const InvoiceDetails = ({ isNew, saveForLaterEnabled, onEntitySelect, invoiceData }) => {
  const { token } = theme.useToken()
  const { Title } = Typography

  const renderEntitySelect = (type, refType = '') => {
    let initialValue = ''

    if (!isNew && saveForLaterEnabled) {
      switch (type) {
        case 'Patient':
          initialValue = invoiceData?.patientRecord?.firstName + ' ' + invoiceData?.patientRecord?.lastName
          break
        case 'Practitioner':
          if (refType === 'ref') {
            if (invoiceData?.refMedProfRecord) {
              initialValue = invoiceData?.refMedProfRecord?.firstName + ' ' + invoiceData?.refMedProfRecord?.lastName
            }
          } else if (refType === 'ass') {
            if (invoiceData?.assMedProfRecord) {
              initialValue = invoiceData?.assMedProfRecord?.firstName + ' ' + invoiceData?.assMedProfRecord?.lastName
            }
          } else {
            initialValue = invoiceData?.medicalProfRecord?.firstName + ' ' + invoiceData?.medicalProfRecord?.lastName
          }
          break
        case 'Clinic':
          initialValue = invoiceData?.clinicRecord?.name
          break
      }
    }

    return <EntitySelect type={type} onSelect={onEntitySelect} initialValue={initialValue} />
  }

  // Form item style to be consistent
  const formItemStyle = {
    marginBottom: '16px'
  }

  // Input prefix icon styling
  const iconStyle = {
    color: token.colorPrimary,
    marginRight: '8px'
  }

  return (
    <Card
      bordered
      style={{
        marginBottom: '16px',
        borderRadius: '8px',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)'
      }}
    >
      <div style={{ marginBottom: '16px' }}>
        <Title level={5} style={{ margin: 0, color: token.colorTextSecondary }}>
          <FontAwesomeIcon icon={faUser} style={{ marginRight: '8px', color: token.colorPrimary }} />
          Patient & Practitioner Details
        </Title>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Form.Item
            name='userRef'
            label='Patient'
            rules={[{ required: true, message: 'Patient is required' }]}
            style={formItemStyle}
          >
            {isNew || (!isNew && saveForLaterEnabled) ? (
              renderEntitySelect('Patient')
            ) : (
              <Input readOnly prefix={<FontAwesomeIcon icon={faUser} style={iconStyle} />} />
            )}
          </Form.Item>

          <Row gutter={[16, 0]}>
            <Col span={12}>
              <Form.Item
                name='medicalProfRef'
                label='Practitioner'
                rules={[{ required: true, message: 'Practitioner is required' }]}
                style={formItemStyle}
              >
                {isNew || (!isNew && saveForLaterEnabled) ? (
                  renderEntitySelect('Practitioner')
                ) : (
                  <Input readOnly prefix={<FontAwesomeIcon icon={faUserMd} style={iconStyle} />} />
                )}
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name='bookingDate'
                label='Booking Date'
                rules={[{ required: true, message: 'Booking Date is required' }]}
                style={formItemStyle}
              >
                {isNew || (!isNew && saveForLaterEnabled) ? (
                  <DatePicker
                    style={{ width: '100%' }}
                    format='YYYY-MM-DD'
                    suffixIcon={<FontAwesomeIcon icon={faCalendarAlt} style={{ color: token.colorPrimary }} />}
                    placeholder='Select booking date'
                  />
                ) : (
                  <Input readOnly prefix={<FontAwesomeIcon icon={faCalendarAlt} style={iconStyle} />} />
                )}
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col span={12}>
              <Form.Item
                name='clinicRef'
                label='Venue'
                rules={[{ required: true, message: 'Venue is required' }]}
                style={formItemStyle}
              >
                {isNew || (!isNew && saveForLaterEnabled) ? (
                  renderEntitySelect('Clinic')
                ) : (
                  <Input readOnly prefix={<FontAwesomeIcon icon={faBuilding} style={iconStyle} />} />
                )}
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name='place' label='Place' style={formItemStyle}>
                <Select
                  disabled={!isNew && !saveForLaterEnabled}
                  defaultValue='outHospital'
                  options={[
                    { label: 'Out Hospital', value: 'outHospital' },
                    { label: 'In Hospital', value: 'inHospital' }
                  ]}
                  placeholder='Select place'
                />
              </Form.Item>
            </Col>
          </Row>
        </Col>

        <Col xs={24} md={12}>
          <Form.Item
            name='invoiceType'
            label='Invoice Type'
            rules={[{ required: true, message: 'Invoice Type is required' }]}
            style={formItemStyle}
          >
            <Select
              disabled={!isNew && !saveForLaterEnabled}
              options={[
                { label: 'Medical', value: 'medical' },
                { label: 'Insurance', value: 'insurance' },
                { label: 'Private', value: 'private' }
              ]}
              suffixIcon={<FontAwesomeIcon icon={faFileInvoiceDollar} style={{ color: token.colorPrimary }} />}
              placeholder='Select invoice type'
            />
          </Form.Item>

          <Form.Item name='authNo' label='Authorization Number' style={formItemStyle}>
            <Input
              disabled={!isNew && !saveForLaterEnabled}
              prefix={<FontAwesomeIcon icon={faKey} style={iconStyle} />}
              placeholder='Enter authorization number'
            />
          </Form.Item>

          <Row gutter={[16, 0]}>
            <Col span={12}>
              <Form.Item name='refMedProfRef' label='Referring Doctor' style={formItemStyle}>
                {isNew || (!isNew && saveForLaterEnabled) ? (
                  renderEntitySelect('Practitioner', 'ref')
                ) : (
                  <Input readOnly prefix={<FontAwesomeIcon icon={faUserMd} style={iconStyle} />} />
                )}
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name='assMedProfRef' label='Assisting Doctor' style={formItemStyle}>
                {isNew || (!isNew && saveForLaterEnabled) ? (
                  renderEntitySelect('Practitioner', 'ass')
                ) : (
                  <Input readOnly prefix={<FontAwesomeIcon icon={faUserMd} style={iconStyle} />} />
                )}
              </Form.Item>
            </Col>
          </Row>
        </Col>
      </Row>
    </Card>
  )
}

export default InvoiceDetails
